import { useFormApi } from 'informed';
import {
  getMessageWithPlaceholders,
  localeNames,
  supportedLocales
} from 'shared-locales';
import { useDispatch, useReduxState } from 'hooks';
import { Checkbox, FormGroup, Select } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';

// Same limit as the route. Checked here too, so a larger file never gets sent.
const MAX_FILE_SIZE_IN_BYTES = 20 * 1024 * 1024;

const languageOptions = supportedLocales.map((locale) => ({
  label: localeNames[locale],
  value: locale
}));

export type CourseFileUploader = {
  handleUpload: () => void;
  reset: () => void;
  uppy: { setMeta: (meta: Record<string, unknown>) => void };
};

type Props = {
  isUploadRunning: boolean;
  onClose: () => void;
  onHasFileChange: (hasFile: boolean) => void;
  // the upload can't be resumed, so the modal must stay open while it runs
  onUploadRunningChange: (isUploadRunning: boolean) => void;
  uploaderRef: React.RefObject<CourseFileUploader>;
};

const CoursesCreateFromFile = ({
  isUploadRunning,
  onClose,
  onHasFileChange,
  onUploadRunningChange,
  uploaderRef
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { defaultSystemLanguage }
  } = useReduxState();

  const formApi = useFormApi();

  const defaultLanguage = supportedLocales.find(
    (locale) => locale === defaultSystemLanguage
  );

  // the title is checked again by the route, which answers with the same codes
  const titleErrors: Record<string, string> = {
    title_is_not_unique: messages.coursesTitleInUse,
    title_is_required: messages.coursesTitleEmpty,
    title_is_too_long: messages.commonTextTooLong
  };

  const otherErrors: Record<string, string> = {
    background_jobs_disabled: messages.coursesCreateFromFileBackgroundJobsOff,
    background_jobs_unavailable:
      messages.coursesCreateFromFileBackgroundJobsUnavailable,
    file_is_required: messages.coursesCreateFromFileAddPdf,
    file_too_large: messages.coursesCreateFromFileFileTooLarge,
    not_a_pdf: messages.uploaderFileTypeError,
    quota_exceeded: messages.aiToolsQuotaExceeded
  };

  const handleUploadError = ({
    response
  }: {
    response?: { conflict?: { jobId: number; title: string }; error?: string };
  }) => {
    onUploadRunningChange(false);

    const titleError = titleErrors[response?.error];
    if (titleError) {
      formApi.setError('title', titleError);
      return;
    }

    // the same file is already running, maybe started in another tab; the file
    // stays so the user can send it again once that course is ready
    if (response?.error === 'generation_already_running') {
      if (response.conflict) {
        dispatch.registerPendingJob({
          jobId: response.conflict.jobId,
          type: 'course-generation'
        });
      }
      dispatch.addNotice({
        text: getMessageWithPlaceholders({
          message: messages.coursesCreateFromFileAlreadyRunning,
          placeholders: { courseTitle: response.conflict?.title ?? '' }
        }),
        type: 'warning'
      });
      return;
    }

    uploaderRef.current?.reset();

    const errorMessage =
      otherErrors[response?.error] || messages.commonUploadFileFailed;
    dispatch.handleErrorDispatch(null, errorMessage);
  };

  const handleUploadSuccess = ({
    response
  }: {
    response: { jobId: number };
  }) => {
    dispatch.registerPendingJob({
      jobId: response.jobId,
      type: 'course-generation'
    });
    dispatch.addNotice({
      text: messages.coursesCreateFromFileStarted,
      type: 'success'
    });
    onUploadRunningChange(false);
    onClose();
  };

  return (
    <>
      <FormGroup
        htmlFor="language"
        label={messages.commonLanguage}
        maxWidth="100%"
      >
        <Select
          disabled={isUploadRunning}
          field="language"
          id="language"
          initialValue={defaultLanguage || 'en-GB'}
          options={languageOptions}
        />
      </FormGroup>

      <FormGroup label={messages.coursesCreateFromFileFile}>
        <FileUploader
          allowedFileTypes={['.pdf']}
          destination="local"
          dragDrop={true}
          dragDropActions={false}
          endpoint="/admin/courses/generate-from-file"
          fileSizeErrorText={messages.coursesCreateFromFileFileTooLarge}
          fileTypeErrorText={messages.uploaderFileTypeError}
          getUploader={(instance) => (uploaderRef.current = instance)}
          icon="file"
          id="course-from-file-uploader"
          maxFileSize={MAX_FILE_SIZE_IN_BYTES}
          onError={handleUploadError}
          onFileAdded={() => onHasFileChange(true)}
          onFileRemoved={() => onHasFileChange(false)}
          onUploadSuccess={handleUploadSuccess}
        />
      </FormGroup>

      <FormGroup>
        <Checkbox
          disabled={isUploadRunning}
          field="useOwnKnowledge"
          id="use-own-knowledge"
          label={messages.aiGenerateContentAllowOwnKnowledge}
          labelHelpText={messages.coursesCreateFromFileUseOwnKnowledgeInfo}
        />
      </FormGroup>
    </>
  );
};

export default CoursesCreateFromFile;
